import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React, { useState, useRef, useEffect } from 'react';

import type { SidebarNavItemProps } from './Sidebar.types';

import { NotificationDot } from '@/components/ui/NotificationDot';

/**
 * Individual navigation item in the sidebar.
 *
 * Supports:
 * - Active state with left border accent
 * - Nested items with expand/collapse
 * - Collapsed mode with tooltip
 * - Badges
 */
export function SidebarNavItem({
  item,
  active = false,
  collapsed = false,
  depth = 0,
  onClick,
}: SidebarNavItemProps): React.JSX.Element {
  const hasChildren = item.children && item.children.length > 0;
  const [expanded, setExpanded] = useState(item.expanded ?? false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLButtonElement>(null);

  // Calculate left padding based on depth
  const leftPadding = collapsed ? 0 : 16 + depth * 16;

  const handleClick = () => {
    if (hasChildren) {
      setExpanded(!expanded);
    }
    onClick?.(item);
  };

  // Handle tooltip positioning
  useEffect(() => {
    if (showTooltip && collapsed && tooltipRef.current && itemRef.current) {
      const itemRect = itemRef.current.getBoundingClientRect();
      tooltipRef.current.style.top = `${itemRect.top + itemRect.height / 2}px`;
    }
  }, [showTooltip, collapsed]);

  return (
    <div className="sidebar-nav-item">
      <button
        ref={itemRef}
        type="button"
        onClick={handleClick}
        onMouseEnter={() => collapsed && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`
          group relative flex w-full items-center gap-3 rounded-lg transition-colors duration-fast
          focus-visible:outline-none focus-visible:ring-state-focus
          ${collapsed ? 'justify-center px-0 py-3' : 'py-3 pr-4'}
          ${
            active
              ? 'bg-primary-300/10 text-primary-300'
              : 'text-neutral-400 hover:bg-contrast-600 hover:text-neutral-50'
          }
        `}
        style={{ paddingLeft: collapsed ? undefined : leftPadding }}
        // Collapsed, the label isn't rendered and the tooltip only mounts on
        // hover, so the icon-only button carries the name itself.
        aria-label={collapsed ? item.label : undefined}
        aria-current={active ? 'page' : undefined}
      >
        {/* Active indicator - left border */}
        {active && (
          <span className="absolute bottom-2 left-0 top-2 w-[3px] rounded-r-full bg-primary-300" />
        )}

        {/* Icon */}
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center ${
            active
              ? 'text-primary-300'
              : 'text-neutral-500 group-hover:text-neutral-50'
          }`}
        >
          {item.icon}
        </span>

        {/* Label - hidden when collapsed */}
        {!collapsed && (
          <>
            <span className="flex-1 truncate text-left text-label-sm font-medium">
              {item.label}
            </span>

            {/* Badge */}
            {item.badge !== undefined && (
              <NotificationDot count={item.badge} size="md" />
            )}

            {/* Expand/collapse icon for items with children */}
            {hasChildren && (
              <span className="text-neutral-500">
                {expanded ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </span>
            )}
          </>
        )}
      </button>

      {/* Tooltip for collapsed state */}
      {collapsed && showTooltip && (
        <div
          ref={tooltipRef}
          className="fixed left-[80px] z-50 -translate-y-1/2 whitespace-nowrap rounded-md bg-contrast-600 px-3 py-1.5 text-label-sm font-medium text-neutral-50 shadow-lift-popover"
          role="tooltip"
        >
          {item.label}
          {item.badge !== undefined && (
            <NotificationDot count={item.badge} size="sm" className="ml-2" />
          )}
          {/* Tooltip arrow */}
          <span className="absolute left-0 top-1/2 -ml-1 -translate-y-1/2 border-4 border-transparent border-r-contrast-600" />
        </div>
      )}

      {/* Nested items */}
      {hasChildren && expanded && !collapsed && (
        <div className="mt-1 space-y-1">
          {item.children!.map((child) => (
            <SidebarNavItem
              key={child.id}
              item={{ ...child, active: false }}
              active={false}
              collapsed={collapsed}
              depth={depth + 1}
              onClick={onClick!}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default SidebarNavItem;
